import { ScrollView, StyleSheet, View, StatusBar as ReactNativeStatusBar } from 'react-native';
import type { PickerItem } from 'src/presentation/components/molecules/SelectField';
import { useNavigation } from '@react-navigation/native';
import LogOutDialog from 'src/presentation/dialogs/LogOutDialog';
import MailResponseDialog from 'src/presentation/dialogs/MailResponseDialog';
import SendMessageDialog from 'src/presentation/dialogs/SendMessageDialog';
import ErrorView from 'src/presentation/components/organisms/ErrorView';
import LoadingIndicator from 'src/presentation/components/molecules/LoadingIndicator';
import MenuRow from 'src/presentation/components/molecules/MenuRow';
import { useAccountStore } from 'src/domain/stores/useAccountStore';
import { useAuthStore } from 'src/domain/stores/useAuthStore';
import { useMessagesStore } from 'src/domain/stores/useMessagesStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import { useWorkspacesStore } from 'src/domain/stores/useWorkspacesStore';
import Colors from 'src/core/utils/Colors';
import AppVersionText from 'src/presentation/components/molecules/AppVersionText';
import BackgroundPattern from 'src/presentation/components/atoms/BackgroundPattern';
import { StatusBar } from 'expo-status-bar';
import Constants from 'src/core/utils/Constants';
import { SvgIconType } from '@thermenresorts/icons';
import { useTabBarInset } from 'src/presentation/hooks/useTabBarInset';
import useAppStateScreenListener from 'src/presentation/hooks/useAppStateScreenListener';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { AccountNavigation, MainNavigation } from 'src/presentation/navigation/types/NavigationTypes';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

export default function AccountScreen() {
	const navigation = useNavigation<StackNavigationProp<AccountNavigation>>();
	const mainNavigation = useNavigation<StackNavigationProp<MainNavigation>>();
	const tabBarInset = useTabBarInset();

	const { user, logout } = useAuthStore();
	const {
		isLogoutDialogVisible,
		isSendMessageDialogVisible,
		isMessageSentDialogVisible,
		message,
		subject,
		reservationNumber,
		selectedWorkspaceId,
		messageError,
		isSendingMessage,
		init,
		postMessage,
		setLogoutDialogVisible,
		openSendMessageDialog,
		closeSendMessageDialog,
		setMessageSentDialogVisible,
		setMessage,
		setSubject,
		setReservationNumber,
		setSelectedWorkspaceId,
	} = useAccountStore();
	const { sendMessageValidationErrors, unreadCount, getApiMessages } = useMessagesStore();
	const { workspaces, workspacesErrorMessage, workspacesLoading, fetchWorkspaces } = useWorkspacesStore();
	const { logEvent, captureEvent } = useServicesStore();

	const hasWorkspaces = workspaces.length > 0;

	const fetchData = () => {
		if (!hasWorkspaces) {
			fetchWorkspaces().catch((error: ApiRequestError) =>
				captureEvent({ message: 'fetchWorkspaces failed', extra: { error } }),
			);
		}

		getApiMessages().catch((error: ApiRequestError) =>
			captureEvent({ message: 'getApiMessages failed', extra: { error } }),
		);
	};

	useAppStateScreenListener({
		onInit: () => init(),
		onFocussed: () => {
			ReactNativeStatusBar.setBarStyle('dark-content');

			fetchData();
		},
		onForegrounded: fetchData,
	});

	const getWorkspaceItems: () => Array<PickerItem> = () =>
		workspaces.map((workspace) => ({ label: workspace.title, value: workspace.id.toString() }));

	const doLogout = async () => {
		logEvent({ name: Constants.analyticsEventLogout, params: {}, user });
		setLogoutDialogVisible(false);

		await logout();
	};

	const serverMessageError =
		Object.values(sendMessageValidationErrors)
			.map((messages) => messages[0])
			.filter((serverMessage): serverMessage is string => serverMessage !== undefined)
			.at(-1) ?? '';

	return (
		<>
			<StatusBar style={'dark'} />
			<View style={{ flex: Dimens.flex1 }}>
				<BackgroundPattern />
				{workspacesLoading ? (
					<LoadingIndicator style={{ flex: Dimens.flex1 }} />
				) : workspacesErrorMessage ? (
					<ErrorView onPress={fetchData} />
				) : (
					<ScrollView contentContainerStyle={{ paddingBottom: tabBarInset }}>
						<View style={styles.whiteBlock}>
							<LogOutDialog
								buttonText={Translations.cancel}
								logoutText={Translations.accountLogout}
								visible={isLogoutDialogVisible}
								onPress={() => setLogoutDialogVisible(false)}
								onPressLogout={doLogout}
							/>
							<MailResponseDialog
								buttonText={Translations.close}
								text={`
									<p>${Translations.thanksForYourMessageWeWillContactYouByEmailAsSoonAsPossible}</p>
									<p>${Translations.pleaseNoteHaveYouRequestedAChangeYouWillThenReceiveANewConfirmationOfYourAdjustedReservationByEmailFromUsAsSoonAsYouHaveReceivedThisEmailThisChangeWillAlsoBeVisibleInTheAppWithinAnHour}</p>
								`}
								title={Translations.response}
								visible={isMessageSentDialogVisible}
								onPress={() => setMessageSentDialogVisible(false)}
							/>
							<SendMessageDialog
								emailText={user?.email?.toString() ?? ''}
								items={getWorkspaceItems()}
								messageError={messageError ?? serverMessageError}
								messageText={message}
								nameText={user?.fullName?.toString() ?? ''}
								reservationNumberText={reservationNumber ?? ''}
								selectedValue={selectedWorkspaceId}
								setMessageText={setMessage}
								setReservationNumberText={setReservationNumber}
								setSelectedValue={setSelectedWorkspaceId}
								setSubjectText={setSubject}
								subjectText={subject}
								visible={isSendMessageDialogVisible}
								onPress={closeSendMessageDialog}
								onPressClaim={() => postMessage()}
								onSendMessageLoading={isSendingMessage}
							/>
							<MenuRow
								icon={SvgIconType.calendarComplete}
								label={Translations.accountUpcomingReservations}
								onPress={() => navigation.navigate(Constants.screenUpcomingReservations)}
							/>
							<MenuRow
								icon={SvgIconType.clockTimeBack}
								label={Translations.accountPastReservations}
								onPress={() => navigation.navigate(Constants.screenPastReservations)}
							/>
							<MenuRow
								icon={SvgIconType.accountSettings}
								label={Translations.accountMyDetails}
								onPress={() => navigation.navigate(Constants.screenProfile)}
							/>
							<MenuRow
								icon={SvgIconType.preferences}
								label={Translations.accountMyPreferences}
								onPress={() => navigation.navigate(Constants.screenPreferences)}
							/>
							<MenuRow
								badge={unreadCount}
								icon={SvgIconType.mail}
								label={Translations.messages}
								onPress={() => mainNavigation.navigate(Constants.navigatorMessages, { screen: Constants.screenMessagesIndex })}
							/>
							<MenuRow icon={SvgIconType.chat} label={Translations.accountSendAMessage} onPress={openSendMessageDialog} />
							<MenuRow
								icon={SvgIconType.info}
								label={Translations.accountAppExplanation}
								onPress={() => navigation.navigate(Constants.screenTutorial)}
							/>
							<MenuRow
								color={Colors.primary}
								icon={SvgIconType.logout}
								label={Translations.accountLogout}
								last={true}
								onPress={() => setLogoutDialogVisible(true)}
							/>
						</View>
						<AppVersionText />
					</ScrollView>
				)}
			</View>
		</>
	);
}

const styles = StyleSheet.create({
	whiteBlock: {
		backgroundColor: Colors.white,
		marginBottom: Dimens.padding8,
	},
});
